import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/AccountsAPI/configVerificationAPI';

// State for pages/Accounts/ConfigVerification.jsx — one page at a time is mounted, so a single slice
// (reset on mount / unmount) serves every config.

const err = (e, fallback) => e?.ErrorDetails?.ErrorMessage || e?.Message || e?.message || fallback;

// Detail endpoints return either a one-row list or a single object.
const firstRow = (data) => (Array.isArray(data) ? data[0] || null : data || null);

export const fetchCVQueue = createAsyncThunk(
    'configVerification/fetchQueue',
    async ({ route, params }, { rejectWithValue }) => {
        try { return await api.getRoute(route, params); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch the verification queue')); }
    }
);

export const fetchCVDetail = createAsyncThunk(
    'configVerification/fetchDetail',
    async ({ route, params, method }, { rejectWithValue }) => {
        try { return await (method === 'post' ? api.postRoute(route, params) : api.getRoute(route, params)); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch details')); }
    }
);

// Supporting lookups a config needs (e.g. existing capital), stored under aux[name].
export const fetchCVAux = createAsyncThunk(
    'configVerification/fetchAux',
    async ({ route, params }, { rejectWithValue }) => {
        try { return await api.getRoute(route, params); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch supporting data')); }
    }
);

export const submitCV = createAsyncThunk(
    'configVerification/submit',
    async ({ route, payload, method }, { rejectWithValue }) => {
        try { return await (method === 'post' ? api.postRoute : api.putRoute)(route, payload); }
        catch (e) { return rejectWithValue(err(e, 'Error Occurred')); }
    }
);

const initialState = {
    queue: [], queueLoading: false, queueError: null,
    // detailKey = itemKey of the queue row the detail was requested for
    detail: null, detailKey: null, detailLoading: false, detailError: null,
    aux: {},
    submitLoading: false,
};

const configVerificationSlice = createSlice({
    name: 'configVerification',
    initialState,
    reducers: {
        clearCVDetail: (s) => { s.detail = null; s.detailKey = null; s.detailError = null; s.detailLoading = false; },
        resetConfigVerification: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCVQueue.pending, (s) => { s.queueLoading = true; s.queueError = null; })
            .addCase(fetchCVQueue.fulfilled, (s, a) => { s.queueLoading = false; s.queue = a.payload?.Data || []; })
            .addCase(fetchCVQueue.rejected, (s, a) => { s.queueLoading = false; s.queueError = a.payload; s.queue = []; });

        builder
            .addCase(fetchCVDetail.pending, (s) => { s.detailLoading = true; s.detailError = null; s.detail = null; s.detailKey = null; })
            .addCase(fetchCVDetail.fulfilled, (s, a) => {
                s.detailLoading = false;
                s.detail = firstRow(a.payload?.Data);
                s.detailKey = a.meta.arg.key;
            })
            .addCase(fetchCVDetail.rejected, (s, a) => { s.detailLoading = false; s.detailError = a.payload; s.detail = null; });

        builder
            .addCase(fetchCVAux.pending, (s, a) => { s.aux[a.meta.arg.name] = undefined; })
            .addCase(fetchCVAux.fulfilled, (s, a) => { s.aux[a.meta.arg.name] = a.payload?.Data ?? null; })
            .addCase(fetchCVAux.rejected, (s, a) => { s.aux[a.meta.arg.name] = null; });

        builder
            .addCase(submitCV.pending, (s) => { s.submitLoading = true; })
            .addCase(submitCV.fulfilled, (s) => { s.submitLoading = false; })
            .addCase(submitCV.rejected, (s) => { s.submitLoading = false; });
    },
});

export const { clearCVDetail, resetConfigVerification } = configVerificationSlice.actions;

export const selectCVQueue = (s) => s.configVerification.queue;
export const selectCVQueueLoading = (s) => s.configVerification.queueLoading;
export const selectCVQueueError = (s) => s.configVerification.queueError;
export const selectCVDetail = (s) => s.configVerification.detail;
export const selectCVDetailKey = (s) => s.configVerification.detailKey;
export const selectCVDetailLoading = (s) => s.configVerification.detailLoading;
export const selectCVDetailError = (s) => s.configVerification.detailError;
export const selectCVAux = (s) => s.configVerification.aux;
export const selectCVSubmitLoading = (s) => s.configVerification.submitLoading;

export default configVerificationSlice.reducer;
